import React from "react";
import { Box, Avatar, Stack } from "@mui/joy";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { GroupedActivityItem, useActivityStore } from "../../store/activityStore";
import { ReactionBar } from "../Reactions";
import { useReactionStore } from "../../store/reactionStore";
import { PostcardPhotoCard } from "./PostcardPhotoCard";
import { PostcardActivityCard } from "./PostcardActivityCard";
import { CommentSection } from "./CommentSection";

interface ActivityItemProps {
  activity: GroupedActivityItem;
}

export const ActivityItem: React.FC<ActivityItemProps> = ({ activity }) => {
  const navigate = useNavigate();
  const { toggleReaction } = useReactionStore();
  const { updateActivityReaction } = useActivityStore();

  const handleClick = () => {
    navigate(`/profile/${activity.user_id}`);
  };

  const handleReactionChange = async (reactionKey: string) => {
    try {
      const result = await toggleReaction("activity", activity.id, reactionKey);

      updateActivityReaction(
        activity.id,
        result.action,
        result.action === "removed" ? null : reactionKey,
        result.previousReaction
      );
    } catch (error) {
      console.error("Failed to toggle reaction:", error);
    }
  };

  const timeAgo = formatDistanceToNow(new Date(activity.created_at), {
    addSuffix: true,
  });

  const avatarUrl = activity.profile?.avatar_url;
  const username = activity.profile?.username || activity.profile?.full_name || "User";

  return (
    <Box
      data-testid={`activity-item-${activity.id}`}
      sx={{
        backgroundColor: "#f5f0e8",
        borderRadius: "6px",
        padding: "20px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        cursor: "pointer",
        "&:hover": {
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        },
      }}
      onClick={handleClick}
    >
      {/* Postcard content */}
      {activity.activity_type === "photo_uploaded" ? (
        <PostcardPhotoCard activity={activity} />
      ) : (
        <PostcardActivityCard activity={activity} />
      )}

      {/* Footer: avatar + time + reactions */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ marginTop: "14px", padding: "0 4px" }}
      >
        <Avatar
          src={avatarUrl || undefined}
          alt={username}
          sx={{
            width: 26,
            height: 26,
            fontSize: "12px",
          }}
        >
          {!avatarUrl && username.charAt(0).toUpperCase()}
        </Avatar>
        <Box
          component="span"
          sx={{ fontSize: "13px", color: "#555", fontWeight: 500 }}
        >
          {username}
        </Box>
        <Box
          component="span"
          sx={{ fontSize: "12px", color: "#aaa" }}
        >
          · {timeAgo}
        </Box>

        {/* Reactions */}
        <Box
          sx={{ marginLeft: "auto !important" }}
          onClick={(e) => e.stopPropagation()}
        >
          <ReactionBar
            entityId={activity.id}
            entityType="activity"
            reactions={activity.reactions}
            userReaction={activity.user_reaction}
            onToggleReaction={handleReactionChange}
          />
        </Box>
      </Stack>

      {/* Comments */}
      <CommentSection activityId={activity.id} />
    </Box>
  );
};
